import { AdpEarningsDefinition, AdpImportFileDefinitionResponse } from "@herbjoy/apex-api-models";
import { IComponentOptions, ILogService, IOnChanges, IOnChangesObject, IFormController, IWindowService, ITimeoutService } from "angular";
import { EventEmitter } from "../../../common/components/event-emitter";
import { UtilityService } from "../../../core/services/utility.service";
import templateUrl from "./adp-config-editor.component.html";
import "./adp-config-editor.component.less";
import { ValidAdpEarningsDefinition } from "./adp-earnings-code-editor.component";

export const AdpConfigEditorComponent: IComponentOptions = {
    templateUrl,
    bindings: {
        adpImportFileDefinition: "<",
        save: "&onSave"
    },
    controller: class AdpConfigEditorComponent implements IOnChanges {
        adpImportFileDefinition: AdpImportFileDefinitionResponse
        selectedDefinition: AdpEarningsDefinition;
        editForm: IFormController;
        save: EventEmitter<{ definition: ValidAdpImportFileDefinition }>;

        constructor(private $log: ILogService, private $timeout: ITimeoutService, $window: IWindowService,
            private utilityService: UtilityService) {
            'ngInject';
            $window.onbeforeunload = () => {
                return this.editForm && this.editForm.$dirty ? "You have unsaved changes" : undefined;
            }
        }

        $onInit() {
            this.$timeout(() => {
                // Allow edit form to initialize first.
                if (!this.adpImportFileDefinition.id) {
                    this.editForm.$setDirty();
                }
            })
        }

        $onChanges(changesObj: IOnChangesObject) {
            if (changesObj.adpImportFileDefinition) {
                // Shallow clone the definition.
                this.adpImportFileDefinition = {
                    ...this.adpImportFileDefinition,
                    // The base implementation specifies a sort index to allow multiple codes to
                    // occupy the same index, but when editing in a list like this, the behaviour
                    // ends up being difficult to anticipate. Linearize to improve UX.
                    definitions: this.adpImportFileDefinition.definitions.map((p, i) => ({
                        ...p,
                        sortIndex: i
                    }))
                };
                if (this.adpImportFileDefinition.definitions.length > 0) {
                    // Pre-select a definition initially.
                    this.selectedDefinition = this.adpImportFileDefinition.definitions[0];
                }
            }
        }

        onSave() {
            this.save({
                $event: {
                    definition: {
                        definitions: this.adpImportFileDefinition.definitions,
                        name: this.adpImportFileDefinition.name
                    }
                }
            });
            this.editForm.$setPristine();
        }

        onSelectEarningsDefinition(definition: AdpEarningsDefinition) {
            this.selectedDefinition = definition;
        }

        onChangeEarningsDefinition({ definition }: { definition: ValidAdpEarningsDefinition }) {
            // This changes the reference, which will trigger the update in the save handler
            this.adpImportFileDefinition.definitions = this.adpImportFileDefinition.definitions.map(p =>
                p === this.selectedDefinition ? definition : p);
            this.selectedDefinition = definition;
        }

        onAddEarningsDefinition() {
            this.selectedDefinition = {
                sortIndex: this.adpImportFileDefinition.definitions.length,
                conditions: [],
                payHours: {
                    conditions: []
                },
                dollars: {
                    conditions: []
                }
            };
            this.adpImportFileDefinition.definitions.push(this.selectedDefinition);
            this.editForm.$setDirty();
        }

        onRemoveCode() {
            if (!confirm(`The earnings code "${this.selectedDefinition.earningsCode}" will be removed.`)) {
                return;
            }
            this.adpImportFileDefinition.definitions = this.adpImportFileDefinition.definitions
                .filter(p => p !== this.selectedDefinition);
            this.selectedDefinition = this.adpImportFileDefinition.definitions[0];
            this.editForm.$setDirty();
        }

        onMoveItem(definition: AdpEarningsDefinition, index: number) {
            if (index < 0 || index >= this.adpImportFileDefinition.definitions.length) {
                this.$log.info(`Attempted to move item from index ${definition.sortIndex} to index ${index}`);
                return;
            }
            definition.sortIndex = index;
            this.utilityService.moveItem(this.adpImportFileDefinition.definitions, definition, index);
            this.editForm.$setDirty();
        }
    }
};

export interface ValidAdpImportFileDefinition {
    name: string;
    definitions: AdpEarningsDefinition[];
}
